import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiClient } from '../lib/auth';

export interface ContentTag {
  value: string;
  source: 'nova' | 'rekognition-celebrity' | 'face-collection' | 'manual';
  confidence?: number;
}

export interface TimelineSegment {
  timestamp: number;
  description: string;
}

export interface MediaItemTransition {
  transitionId: string;
  status: string;
  targetStorageClass: string;
  initiatedAt: string;
  error: string | null;
}

export interface MediaItem {
  mediaId: string;
  filename: string;
  contentType: string;
  fileSize: number;
  uploadedAt: string;
  author: string;
  folderPath: string[];
  folderPathStr: string;
  label: string;
  storageClass: string;
  thumbnailUrl: string | null;
  previewUrl?: string;
  description: string | null;
  /** User-authored caption; takes precedence over the AI `description` when present. */
  userCaption?: string | null;
  tags: ContentTag[];
  processingStatus: 'pending' | 'complete' | 'failed';
  fileHash?: string;
  /** Content-addressed S3 key; `media/content/...` indicates shared storage. */
  s3Key?: string;
  /**
   * True when this item's content is genuinely shared with other items
   * (content-object refCount > 1). Supplied by GET /media list responses.
   */
  shared?: boolean;
  /**
   * Deduplication summary from GET /media/{id}. Null for legacy records or when
   * no content object exists.
   */
  dedup?: {
    refCount: number;
    shared: boolean;
    physicalSize: number;
  } | null;
  transition?: MediaItemTransition | null;
  timeline?: TimelineSegment[];
  /**
   * True when this item is in the current (internal) user's Favourites set.
   * Supplied by GET /media list responses and favourites/collection listings.
   * Collections & Favourites feature (Requirement 2).
   */
  isFavourite?: boolean;
  /** Cosine similarity of a semantic/hybrid search result (rounded). */
  similarity?: number;
  /** Combined score of a hybrid search result. */
  score?: number;
}

export interface MediaListResponse {
  items: MediaItem[];
  total: number;
  pageSize: number;
  /** Opaque cursor for the next page, or null when this is the last page (Requirement 32.3, 32.4). */
  nextCursor: string | null;
}

// ---------------------------------------------------------------------------
// Folder hierarchy types (Requirement 19)
// ---------------------------------------------------------------------------

export interface FolderEvent {
  name: string;
  count: number;
}

export interface FolderDepartment {
  name: string;
  count: number;
  events: FolderEvent[];
}

export interface FolderHierarchy {
  departments: FolderDepartment[];
}

/**
 * A single folder returned by GET /media/folders. Matches the shape used in
 * `FolderView.tsx`. Used by the path-aware Folder View feature.
 */
export interface FolderItem {
  name: string;
  path: string;
  mediaCount: number;
  totalMediaCount: number;
  createdAt?: string;
}

// ---------------------------------------------------------------------------
// Filter params
// ---------------------------------------------------------------------------

/**
 * How a `fetchMedia` request relates to the current pagination position.
 * - `reset`: initial load / filter or page-size change (clears the cursor stack).
 * - `next`: advance to the next page (pushes the current cursor onto the stack).
 * - `prev`: go back one page (pops the cursor stack).
 * - `append`: "Load more" — fetch the next page and append it to the current view.
 */
export type MediaPageDirection = 'reset' | 'next' | 'prev' | 'append';

export interface MediaFilters {
  tags?: string;
  folder?: string;
  label?: string;
  mediaType?: string;
  sort?: string;
  pageSize?: number;
  /** Opaque cursor to fetch from (Requirement 32.5). Omit for the first page. */
  cursor?: string | null;
  /**
   * Navigation intent for the fulfilled reducer. Defaults to `reset` when
   * omitted (initial load / filter change).
   */
  direction?: MediaPageDirection;
}

export interface MediaState {
  items: MediaItem[];
  total: number;
  pageSize: number;
  /** Cursor for the next page from the latest response, or null on the last page. */
  nextCursor: string | null;
  /**
   * Cursors used to reach previous pages, enabling backward navigation
   * (Requirement 32.11). The cursor used to fetch the *current* page is pushed
   * here when advancing to the next page, so Previous can re-fetch it.
   */
  cursorStack: string[];
  /**
   * The cursor that was used to fetch the currently displayed page (null for
   * the first page). Tracked so Next knows what to push onto the stack.
   */
  currentCursor: string | null;
  selectedItem: MediaItem | null;
  loading: boolean;
  error: string | null;
  /** Folder hierarchy for folder view (internal users only) */
  folders: FolderHierarchy | null;
  foldersLoading: boolean;
  foldersError: string | null;
  /**
   * Path-aware folder-level state for the Folder View page. Tracks the child
   * folders of the latest requested path, with a stale-response guard.
   */
  folderLevel: {
    /** The path whose children are the source of truth for a settle. */
    path: string;
    items: FolderItem[];
    loading: boolean;
    error: string | null;
  };
}

const initialState: MediaState = {
  items: [],
  total: 0,
  pageSize: 20,
  nextCursor: null,
  cursorStack: [],
  currentCursor: null,
  selectedItem: null,
  loading: false,
  error: null,
  folders: null,
  foldersLoading: false,
  foldersError: null,
  folderLevel: {
    path: '',
    items: [],
    loading: false,
    error: null,
  },
};

// ---------------------------------------------------------------------------
// Async thunks
// ---------------------------------------------------------------------------

/**
 * Fetch a page of the media list from GET /media using cursor-based paging.
 *
 * Sends an opaque `cursor` (when present), `pageSize`, and the usual filters.
 * The response includes `nextCursor` (string or null) rather than an offset
 * page number. The `direction` arg drives how the fulfilled reducer updates
 * the cursor stack and whether items are appended or replaced.
 *
 * Requirements: 32.5, 32.8, 32.9, 32.10, 32.11
 */
export const fetchMedia = createAsyncThunk(
  'media/fetchMedia',
  async (filters: MediaFilters = {}, { rejectWithValue }) => {
    try {
      const params: Record<string, string | number> = {
        pageSize: filters.pageSize ?? 20,
      };
      if (filters.cursor) params.cursor = filters.cursor;
      if (filters.tags) params.tags = filters.tags;
      if (filters.folder) params.folder = filters.folder;
      if (filters.label) params.label = filters.label;
      if (filters.mediaType) params.mediaType = filters.mediaType;
      if (filters.sort) params.sort = filters.sort;

      const response = await apiClient.get<MediaListResponse>('/media', { params });
      return response.data;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load media';
      return rejectWithValue(message);
    }
  },
);

/**
 * Fetch folder hierarchy from GET /media/folders (internal users only).
 * Requirements: 19.3, 19.4, 19.5, 19.6, 19.8
 */
export const fetchFolders = createAsyncThunk(
  'media/fetchFolders',
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<FolderHierarchy>('/media/folders');
      return response.data;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load folders';
      return rejectWithValue(message);
    }
  },
);

/**
 * Fetch the child folders of a given path from GET /media/folders (path-aware,
 * one level at a time). Used by the Folder View page.
 *
 * When `path` is empty (top level), the `path` query param is omitted so the
 * endpoint defaults to ROOT.
 *
 * Requirements: 4.1, 4.2, 4.3, 8.1, 8.2, 10.1
 */
export const fetchFolderLevel = createAsyncThunk(
  'media/fetchFolderLevel',
  async (path: string, { rejectWithValue }) => {
    try {
      const params = path && path.length > 0 ? { path } : undefined;
      const response = await apiClient.get<{ folders: FolderItem[] }>('/folders', { params });
      return { path, folders: response.data.folders ?? [] };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load folders';
      return rejectWithValue({ path, message });
    }
  },
);

/**
 * Fetch single media record with preview URL from GET /media/{id}.
 * Requirements: 5.1, 5.3, 5.4, 5.5
 */
export const fetchMediaDetail = createAsyncThunk(
  'media/fetchMediaDetail',
  async (mediaId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<MediaItem>(`/media/${mediaId}`);
      return response.data;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load media detail';
      return rejectWithValue(message);
    }
  },
);

/**
 * Delete media file via DELETE /media/{id}.
 * Requirements: 11.1, 11.2, 11.3, 11.4, 11.6
 */
export const deleteMedia = createAsyncThunk(
  'media/deleteMedia',
  async (mediaId: string, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/media/${mediaId}`);
      return mediaId;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete media';
      return rejectWithValue(message);
    }
  },
);

/**
 * Update media tags via PUT /media/{id}/tags.
 * Requirements: 12.1, 12.2, 12.3, 12.4, 12.5, 12.6
 */
export const updateMediaTags = createAsyncThunk(
  'media/updateMediaTags',
  async (
    { mediaId, add, remove }: { mediaId: string; add?: string[]; remove?: string[] },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiClient.put<{ tags: ContentTag[] }>(`/media/${mediaId}/tags`, {
        add,
        remove,
      });
      return { mediaId, tags: response.data.tags };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update tags';
      return rejectWithValue(message);
    }
  },
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const mediaSlice = createSlice({
  name: 'media',
  initialState,
  reducers: {
    setMediaList(state, action: PayloadAction<MediaListResponse>) {
      state.items = action.payload.items;
      state.total = action.payload.total;
      state.pageSize = action.payload.pageSize;
      state.nextCursor = action.payload.nextCursor;
      state.cursorStack = [];
      state.currentCursor = null;
      state.loading = false;
      state.error = null;
    },
    setSelectedItem(state, action: PayloadAction<MediaItem | null>) {
      state.selectedItem = action.payload;
    },
    removeItem(state, action: PayloadAction<string>) {
      state.items = state.items.filter((item) => item.mediaId !== action.payload);
      state.total = Math.max(0, state.total - 1);
      if (state.selectedItem?.mediaId === action.payload) {
        state.selectedItem = null;
      }
    },
    updateItemTags(state, action: PayloadAction<{ mediaId: string; tags: ContentTag[] }>) {
      const item = state.items.find((i) => i.mediaId === action.payload.mediaId);
      if (item) {
        item.tags = action.payload.tags;
      }
      if (state.selectedItem?.mediaId === action.payload.mediaId) {
        state.selectedItem.tags = action.payload.tags;
      }
    },
    setMediaLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setMediaError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
      state.loading = false;
    },
    /**
     * Reset pagination to the first page (clears the cursor stack). Used when
     * filters or the page size change before the fresh fetch resolves.
     */
    resetMediaPagination(state) {
      state.nextCursor = null;
      state.cursorStack = [];
      state.currentCursor = null;
    },
  },
  extraReducers: (builder) => {
    // fetchMedia
    builder
      .addCase(fetchMedia.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMedia.fulfilled, (state, action) => {
        const direction: MediaPageDirection = action.meta.arg?.direction ?? 'reset';
        // The cursor that was used to fetch this response (null for first page).
        const usedCursor = action.meta.arg?.cursor ?? null;

        state.total = action.payload.total;
        state.pageSize = action.payload.pageSize;
        state.nextCursor = action.payload.nextCursor;

        switch (direction) {
          case 'append':
            // "Load more": extend the current view; position/stack unchanged.
            state.items = [...state.items, ...action.payload.items];
            break;
          case 'next':
            // Advancing: remember the cursor for the page we're leaving so
            // Previous can return to it, then show the new page.
            state.cursorStack.push(state.currentCursor ?? '');
            state.currentCursor = usedCursor;
            state.items = action.payload.items;
            break;
          case 'prev':
            // Going back: pop the stack (the top entry is the cursor for the
            // page we land on) and show that page. `usedCursor` is that same
            // popped value, supplied by the caller.
            state.cursorStack.pop();
            state.currentCursor = usedCursor;
            state.items = action.payload.items;
            break;
          case 'reset':
          default:
            // Initial load / filter or page-size change.
            state.cursorStack = [];
            state.currentCursor = null;
            state.items = action.payload.items;
            break;
        }

        state.loading = false;
        state.error = null;
      })
      .addCase(fetchMedia.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? 'Failed to load media';
      });

    // fetchFolders
    builder
      .addCase(fetchFolders.pending, (state) => {
        state.foldersLoading = true;
        state.foldersError = null;
      })
      .addCase(fetchFolders.fulfilled, (state, action) => {
        state.folders = action.payload;
        state.foldersLoading = false;
        state.foldersError = null;
      })
      .addCase(fetchFolders.rejected, (state, action) => {
        state.foldersLoading = false;
        state.foldersError = (action.payload as string) ?? 'Failed to load folders';
      });

    // fetchFolderLevel (path-aware, with stale-response guard)
    builder
      .addCase(fetchFolderLevel.pending, (state, action) => {
        state.folderLevel.loading = true;
        state.folderLevel.error = null;
        // Record the requested path as the "target" so later settles can be
        // matched against the latest request.
        state.folderLevel.path = action.meta.arg;
      })
      .addCase(fetchFolderLevel.fulfilled, (state, action) => {
        state.folderLevel.loading = false;
        // Only commit items when the response corresponds to the latest
        // requested path (stale-response guard).
        if (action.payload.path === state.folderLevel.path) {
          state.folderLevel.items = action.payload.folders;
          state.folderLevel.error = null;
        }
      })
      .addCase(fetchFolderLevel.rejected, (state, action) => {
        const payload = action.payload as { path: string; message: string } | undefined;
        state.folderLevel.loading = false;
        // Only surface the error when it corresponds to the latest requested path.
        if (payload && payload.path === state.folderLevel.path) {
          state.folderLevel.error = payload.message ?? 'Failed to load folders';
        }
      });

    // fetchMediaDetail
    builder
      .addCase(fetchMediaDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMediaDetail.fulfilled, (state, action) => {
        state.selectedItem = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchMediaDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? 'Failed to load media detail';
      });

    // deleteMedia
    builder
      .addCase(deleteMedia.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteMedia.fulfilled, (state, action) => {
        state.items = state.items.filter((item) => item.mediaId !== action.payload);
        state.total = Math.max(0, state.total - 1);
        if (state.selectedItem?.mediaId === action.payload) {
          state.selectedItem = null;
        }
        state.loading = false;
        state.error = null;
      })
      .addCase(deleteMedia.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? 'Failed to delete media';
      });

    // updateMediaTags
    builder
      .addCase(updateMediaTags.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMediaTags.fulfilled, (state, action) => {
        const item = state.items.find((i) => i.mediaId === action.payload.mediaId);
        if (item) {
          item.tags = action.payload.tags;
        }
        if (state.selectedItem?.mediaId === action.payload.mediaId) {
          state.selectedItem.tags = action.payload.tags;
        }
        state.loading = false;
        state.error = null;
      })
      .addCase(updateMediaTags.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? 'Failed to update tags';
      });
  },
});

export const {
  setMediaList,
  setSelectedItem,
  removeItem,
  updateItemTags,
  setMediaLoading,
  setMediaError,
  resetMediaPagination,
} = mediaSlice.actions;

// Selectors
export const selectMediaItems = (state: { media: MediaState }) => state.media.items;
export const selectMediaTotal = (state: { media: MediaState }) => state.media.total;
export const selectMediaPageSize = (state: { media: MediaState }) => state.media.pageSize;
export const selectSelectedItem = (state: { media: MediaState }) => state.media.selectedItem;
export const selectMediaLoading = (state: { media: MediaState }) => state.media.loading;
export const selectMediaError = (state: { media: MediaState }) => state.media.error;
export const selectFolders = (state: { media: MediaState }) => state.media.folders;
export const selectFoldersLoading = (state: { media: MediaState }) => state.media.foldersLoading;
export const selectFoldersError = (state: { media: MediaState }) => state.media.foldersError;

// Folder View (path-aware) selectors
export const selectFolderLevelItems = (state: { media: MediaState }) =>
  state.media.folderLevel.items;
export const selectFolderLevelLoading = (state: { media: MediaState }) =>
  state.media.folderLevel.loading;
export const selectFolderLevelError = (state: { media: MediaState }) =>
  state.media.folderLevel.error;
export const selectFolderLevelPath = (state: { media: MediaState }) =>
  state.media.folderLevel.path;

// Cursor pagination selectors (Requirement 32.9, 32.10, 32.11)

/** Cursor for the next page, or null on the last page. */
export const selectMediaNextCursor = (state: { media: MediaState }) => state.media.nextCursor;

/** The cursor used to fetch the currently displayed page (null for the first page). */
export const selectMediaCurrentCursor = (state: { media: MediaState }) =>
  state.media.currentCursor;

/** True when a next page is available (Next / Load more should be enabled). */
export const selectHasNextPage = (state: { media: MediaState }) =>
  state.media.nextCursor !== null;

/** True when a previous page is available (Previous should be enabled). */
export const selectHasPrevPage = (state: { media: MediaState }) =>
  state.media.cursorStack.length > 0;

export default mediaSlice.reducer;
